import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useQuery } from '@apollo/client';
import GET_LATEST_PAGE_CONTENT_BY_SLUG from '../../gql/queries/get-latest-page-content-by-slug.gql';

const labelToSlug = (label) => (label || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const formatShortDate = (date) => {
  if (!date) return '';
  try {
    const timestamp = typeof date === 'string' && /^\d+$/.test(date) ? parseInt(date, 10) : date;
    const dateObj = new Date(timestamp);
    if (Number.isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const buildHref = (item) => {
  const slug = labelToSlug(item.label);
  if (item.language && item.language !== 'en') {
    return `/${item.language}/${slug}`;
  }
  return `/${slug}`;
};

function SectionSkeleton() {
  return (
    <ul className="space-y-2">
      {[1, 2, 3, 4].map((i) => (
        <li key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
      ))}
    </ul>
  );
}

function Section({
  title, accentClass, items, loading, currentSlug,
}) {
  const hasItems = Array.isArray(items) && items.length > 0;
  if (!loading && !hasItems) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 text-base flex items-center gap-2">
        <span className={`w-1 h-6 bg-gradient-to-b ${accentClass} rounded-full`} />
        {title}
      </h3>
      {loading ? (
        <SectionSkeleton />
      ) : (
        <ul className="space-y-1">
          {items.map((item) => {
            const slug = labelToSlug(item.label);
            const isActive = currentSlug === slug;
            const href = buildHref(item);
            const dateText = formatShortDate(item.postDate);

            const content = (
              <div className="flex items-start gap-2">
                <span
                  className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    isActive ? 'bg-blue-600' : 'bg-gray-300 group-hover:bg-blue-500'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <span
                    className={`block text-sm leading-snug line-clamp-2 ${
                      isActive
                        ? 'text-blue-700 font-semibold'
                        : 'text-gray-700 group-hover:text-blue-600'
                    }`}
                  >
                    {item.label}
                  </span>
                  {dateText && (
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {dateText}
                    </span>
                  )}
                </div>
              </div>
            );

            return (
              <li key={item.contentId || slug}>
                {isActive ? (
                  <div
                    aria-current="page"
                    className="group block px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 cursor-default"
                  >
                    {content}
                  </div>
                ) : (
                  <Link
                    href={href}
                    className="group block px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  accentClass: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    contentId: PropTypes.string,
    label: PropTypes.string,
    language: PropTypes.string,
    postDate: PropTypes.string,
  })),
  loading: PropTypes.bool,
  currentSlug: PropTypes.string,
};

export default function RecentArticles({ currentSlug }) {
  const lowerSlug = (currentSlug || '').toLowerCase();
  const isDownToEarth = lowerSlug.includes('down-to-earth');
  const isKurukshetra = lowerSlug.includes('kurukshetra');
  const isYojana = lowerSlug.includes('yojana');
  const shouldFetch = isKurukshetra || isYojana || isDownToEarth;

  const { data: yojanaData, loading: yojanaLoading } = useQuery(GET_LATEST_PAGE_CONTENT_BY_SLUG, {
    variables: { name: 'YOJANA', type: 'article-page' },
    errorPolicy: 'ignore',
    skip: !shouldFetch,
  });

  const { data: kurukshetraData, loading: kurukshetraLoading } = useQuery(GET_LATEST_PAGE_CONTENT_BY_SLUG, {
    variables: { name: 'Kurukshetra', type: 'article-page' },
    errorPolicy: 'ignore',
    skip: !shouldFetch,
  });

  const { data: downToEarthData, loading: downToEarthLoading } = useQuery(GET_LATEST_PAGE_CONTENT_BY_SLUG, {
    variables: { name: 'Down To Earth', type: 'article-page' },
    errorPolicy: 'ignore',
    skip: !shouldFetch,
  });

  if (!shouldFetch) return null;

  const yojanaItems = yojanaData?.getLatestPageContentBySlug || [];
  const kurukshetraItems = kurukshetraData?.getLatestPageContentBySlug || [];
  const downToEarthItems = downToEarthData?.getLatestPageContentBySlug || [];

  const yojanaSection = {
    key: 'yojana',
    title: 'Recent Yojana Articles',
    accentClass: 'from-blue-500 to-blue-600',
    items: yojanaItems,
    loading: yojanaLoading,
  };
  const kurukshetraSection = {
    key: 'kurukshetra',
    title: 'Recent Kurukshetra Articles',
    accentClass: 'from-amber-500 to-orange-600',
    items: kurukshetraItems,
    loading: kurukshetraLoading,
  };
  const downToEarthSection = {
    key: 'down-to-earth',
    title: 'Recent Down to Earth Articles',
    accentClass: 'from-emerald-500 to-green-600',
    items: downToEarthItems,
    loading: downToEarthLoading,
  };

  let sections;
  if (isDownToEarth) {
    sections = [downToEarthSection, yojanaSection, kurukshetraSection];
  } else if (isKurukshetra) {
    sections = [kurukshetraSection, yojanaSection, downToEarthSection];
  } else {
    sections = [yojanaSection, kurukshetraSection, downToEarthSection];
  }

  return (
    <div className="space-y-4">
      {sections.map((s) => (
        <Section
          key={s.key}
          title={s.title}
          accentClass={s.accentClass}
          items={s.items}
          loading={s.loading}
          currentSlug={currentSlug}
        />
      ))}
    </div>
  );
}

RecentArticles.propTypes = {
  currentSlug: PropTypes.string,
};
